/**
 * The view's switches, at the graph footer's right end (D369): the depth rings
 * behind the graph, the dimming of every branch the selected claim does not
 * stand on or hold up, the minimap, and the zoom — out and in either side of
 * the figure, which is itself the tool dock's Fit.
 *
 * The three switches are preferences (`showRings`, `dimUnrelated`,
 * `showMinimap`, persisted with the prefs blob), and each says what it does in
 * its title and whether it is on in `aria-pressed`. None of them changes what
 * is on the graph: the rings are the one ambient mark that is data, and taking
 * them away hides a reading, not a claim; the dim fades and never removes, the
 * way Focus does; and the minimap is a second picture of the same graph.
 */

import { useStore, ZOOM_MAX, ZOOM_MIN } from '../core/store'
import { Icon, type IconName } from './components/Icon'

function Switch({
  name,
  label,
  why,
  on,
  onPress,
}: {
  name: IconName
  label: string
  why: string
  on: boolean
  onPress: () => void
}) {
  return (
    <button
      className="viewtoggle"
      aria-label={label}
      aria-pressed={on}
      title={`${label} — ${why}`}
      onClick={onPress}
    >
      <Icon name={name} size={15} />
    </button>
  )
}

export function ViewToggles({ stageW, stageH }: { stageW: number; stageH: number }) {
  const showRings = useStore((s) => s.showRings)
  const dimUnrelated = useStore((s) => s.dimUnrelated)
  const showMinimap = useStore((s) => s.showMinimap)
  const spatial = useStore((s) => s.viewMode === '3d')
  // Rounded in the selector, so a Fit easing across a dozen frames re-renders
  // this once per whole percent rather than once per frame.
  const pct = useStore((s) => Math.round(s.view.k * 100))
  const atMin = useStore((s) => s.view.k <= ZOOM_MIN)
  const atMax = useStore((s) => s.view.k >= ZOOM_MAX)
  const viewInset = useStore((s) => s.viewInset)

  // About the centre of the field the reader can see, by the tool dock's own
  // steps (`zoomBy(1.25)` and `zoomBy(0.8)` in CanvasChrome.tsx), so a press
  // here and a press there move the view the same way.
  const zoom = (factor: number) => {
    const cx = viewInset.left + (stageW - viewInset.left - viewInset.right) / 2
    useStore.getState().zoomBy(factor, cx, stageH / 2)
  }

  return (
    <div className="viewtoggles" role="group" aria-label="View">
      <span className="viewtoggles-switches">
        <Switch
          name="rings"
          label="Rings"
          why="show or hide the concentric rings behind the graph, one for each depth of reasoning"
          on={showRings}
          onPress={() => useStore.getState().setShowRings(!showRings)}
        />
        <Switch
          name="dim"
          label="Dim unrelated"
          why="while a claim is selected, fade every branch it does not stand on or hold up"
          on={dimUnrelated}
          onPress={() => useStore.getState().setDimUnrelated(!dimUnrelated)}
        />
        <Switch
          name="minimap"
          label="Minimap"
          why="show or hide the small overview in the bottom-right corner"
          on={showMinimap}
          onPress={() => useStore.getState().setShowMinimap(!showMinimap)}
        />
      </span>
      {/* The flat view's zoom: out, the figure, in. The figure is a door to Fit,
          the same press as the tool dock's centre one, the palette's and F —
          it frames the whole graph and hands the view back to follow
          (invariant 11), letting go of a focus first as every door to Fit does.
          The spatial view has a distance, not a zoom, so there is no percentage
          to print — the tool dock moves its camera closer, further and to the
          frame. Each end stands down at the hand-zoom limit rather than offer a
          press that does nothing. */}
      {!spatial && (
        <span className="viewzoom" role="group" aria-label="Zoom">
          <button
            className="viewzoom-step"
            aria-label="Zoom out"
            title="Zoom out"
            disabled={atMin}
            onClick={() => zoom(0.8)}
          >
            <Icon name="minus" size={14} />
          </button>
          <button
            className="viewtoggle-zoom"
            title={`Zoom ${pct}% — press to fit the whole graph`}
            aria-label={`Zoom ${pct}%, fit the whole graph`}
            onClick={() => {
              const s = useStore.getState()
              s.setFocus(null)
              s.frame(stageW, stageH)
            }}
          >
            {pct}%
          </button>
          <button
            className="viewzoom-step"
            aria-label="Zoom in"
            title="Zoom in"
            disabled={atMax}
            onClick={() => zoom(1.25)}
          >
            <Icon name="plus" size={14} />
          </button>
        </span>
      )}
    </div>
  )
}
